import { isEmpty } from "@swiggy-private/js-utils";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import type { ICartItemList } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/CartItemList/types";
import { InstructionType } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/ParentOrderWidget/types"; // ToDo: To import from dls-web-recipe package
import { hasType } from "@swiggy-private/portal-dineout-ui";
import { isFrequencyCappedForCartTooltip, updateFrequencyCappingForCartTooltip } from "./frequencyCapping";
import { WIDGET_TYPES } from "./types";
import { INFO_MESSAGES, STORAGE_KEYS } from "../constants";

/**
 * Updates the cart page response by adding tooltip config to the first dish in the cart in CartInfo widget
 * @param cards - The cart page response cards
 * @returns The updated cart page response
 */

export const updateCartInfoWithTooltipConfig = async (cards: IPLWidgetControllerCards[]) => {
    // Early return if no cards provided
    if (isEmpty(cards)) return [];

    // Check if tooltip was already shown (frequency capped)
    const isTooltipShown = await isFrequencyCappedForCartTooltip(STORAGE_KEYS.CART_INSTRUCTION_TOOLTIP_SHOWN);
    if (isTooltipShown) return cards;

    // Map through cards to find and update cart item list widget
    const updatedCards = cards.map((card: IPLWidgetControllerCards) => {
        // Check if card is a cart item list widget
        if (hasType(card) && card?.card?.card?.["@type"] === WIDGET_TYPES.CART_ITEM_LIST) {
            // Type cast needed because `cartInfo` is not a common property of all cards
            const cartItemList = card?.card?.card as unknown as ICartItemList;

            // Process only if cart has dishes
            if (!isEmpty(cartItemList?.cartInfo?.dishes ?? [])) {
                // Find first dish with enabled cooking instructions
                const firstDishIdxWithCookingInstruction = cartItemList.cartInfo.dishes.findIndex((dish) => {
                    return dish.instructions?.some(
                        (instruction) => instruction.type === InstructionType.COOKING_REQUEST && instruction.isEnabled,
                    );
                });

                // Skip if no dish with cooking instructions found
                if (firstDishIdxWithCookingInstruction === -1) return card;

                // Apply tooltip only to the first dish in the cart as per UX requirements
                cartItemList.cartInfo.dishes[firstDishIdxWithCookingInstruction].tooltipConfig = {
                    canShow: !isTooltipShown,
                    text: INFO_MESSAGES.CART_INSTRUCTION_TOOLTIP,
                };
                // Update card with modified cart item list
                card.card.card = cartItemList;

                // Update frequency capping to prevent showing tooltip again in same session
                updateFrequencyCappingForCartTooltip(STORAGE_KEYS.CART_INSTRUCTION_TOOLTIP_SHOWN);
            }
        }
        return card;
    });

    return updatedCards;
};
